import React, {FC, useState, useEffect} from "react"
import CopyToClipboard from "react-copy-to-clipboard"
import styled from "styled-components"

import {DealListSort, DealStatus, DealType} from "@irati/types"
import {Body, H2, H4} from "@irati/ui/src/Typography"
import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import authentication from "@irati/auth"
import {getDomain} from "@irati/utils/links"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import dealsService from "@irati/deals/services/dealsGraph"
import Loading from "@irati/ui/src/Loading"
import teamsService from "@irati/identity/services/teams"
import {Select} from "@irati/ui/src/FormItem"
import {adminDashboardTracking} from "@irati-tracking/admin"
import {WidgetFilterType} from "@irati-tracking/admin/adminDashboard"
import Region, {ALL_TEAMS} from "../../../../Reporting/Region"

interface Props extends InjectIntl {
  onClick?: () => void
}

const Wrapper = styled.div`
  flex-basis: 20%;
  margin-bottom: ${({theme}) => theme.spacing.three};
`

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  height: 100px;
  width: 100px;
  overflow: hidden;
  margin: 0 auto;
  border-radius: 100%;
`

const ShareBenefits: FC<Props> = ({onClick, formatMessage}) => {
  const [isCopied, setIsCopied] = useState(false)
  const [teamOptions, setTeamOptions] = useState([])
  const [teamId, setTeamId] = useState<string>(ALL_TEAMS)
  const [regionId, setRegionId] = useState<string>(ALL_TEAMS)
  const [companyBenefits, setCompanyBenefits] = useState([])
  const [load, setLoad] = useState<Promise<any> | null>(null)

  useEffect(() => {
    const updatedLoad = dealsService
      .list({statuses: [DealStatus.On], ignoreBlacklist: true, types: [DealType.Company], sort: DealListSort.Date, limit: 999})
      .then((res) => {
        return teamsService.list(authentication.business().uuid).then((teams) => {
          const baseTeamOptions = teams.map((team) => ({label: team.teamName, value: team.uuid}))
          const options = [{label: formatMessage({id: "admin.reporting.popularPerks.allTeams"}), value: ALL_TEAMS}, ...baseTeamOptions]
          setTeamOptions(options)
          setCompanyBenefits(res.results)
        })
      })
    setLoad(updatedLoad)
  }, [])

  const getFilters = (region: string, team: string) => {
    let filters: WidgetFilterType[] = []
    if (region !== ALL_TEAMS) {
      filters = [WidgetFilterType.AdminWidgetCountry]
    }
    if (team !== ALL_TEAMS) {
      filters = [...filters, WidgetFilterType.AdminWidgetTeam]
    }
    return filters
  }

  const handleCopyClick = () => {
    const filter = getFilters(regionId, teamId)
    adminDashboardTracking.companyBenefitsActions("cta_sharebenefitText", filter)
    setIsCopied(true)
  }

  const handleUpdateRegion = (updatedRegionId: string) => {
    const filter = getFilters(updatedRegionId, teamId)
    adminDashboardTracking.companyBenefitsActions("benefitfilterCountry", filter)
    setRegionId(updatedRegionId)
  }

  const handleUpdateTeam = (updatedTeamId: string) => {
    const filter = getFilters(regionId, updatedTeamId)
    adminDashboardTracking.companyBenefitsActions("benefitfilterTeam", filter)
    setTeamId(updatedTeamId)
  }

  const copyButtonText = isCopied
    ? "admin.components.dashboard.reporting.costOfLiving.modal.copiedToClipboard"
    : "admin.components.dashboard.reporting.costOfLiving.modal.copyButton"
  const filteredCompanyBenefits = companyBenefits.filter((benefit) => {
    if (regionId !== ALL_TEAMS) {
      return benefit.teamIds?.includes(regionId)
    }
    if (teamId !== ALL_TEAMS) {
      return benefit.teamIds?.includes(teamId)
    }
    return true
  })

  const getCopyText = () => {
    const text = formatMessage({id: "admin.components.dashboard.reporting.companyBenefits.modal.copyToClipboard"})
    const dealsText = filteredCompanyBenefits.map((perk) => {
      return `${formatMessage(
        {id: "admin.components.dashboard.reporting.companyBenefits.modal.l.copyToClipboardPerk"},
        {
          dealName: perk.header,
          dealUrl: `${getDomain(authentication.business().subdomain)}/perks/info/${perk.urlSlug}`
        }
      )}\n`
    })
    return `${text}\n\n${dealsText.join("")}`
  }

  return (
    <div>
      <H2 marginBottom={1}>
        <FormattedMessage id="admin.components.dashboard.reporting.companyBenefits.modal.share.title" />
      </H2>
      {isCopied && (
        <Alert
          type={AlertTypes.success}
          title="admin.components.dashboard.reporting.companyBenefits.modal.share.alert"
          onHide={() => setIsCopied(false)}
        />
      )}
      <Body marginBottom={2} maxWidth="100%">
        <FormattedMessage id="admin.components.dashboard.reporting.companyBenefits.modal.share.text" />
      </Body>
      <Loading load={load}>
        <Flex justifyContent="flex-end" gap={1}>
          <Region id="adminDashboard_reporting_country" value={regionId} onChange={handleUpdateRegion} isDisabled={teamId !== ALL_TEAMS} />
          <Select
            isClearable={false}
            value={teamId}
            options={teamOptions}
            onChange={handleUpdateTeam}
            isDisabled={regionId !== ALL_TEAMS}
          />
        </Flex>
        <H4 textAlign="center" marginBottom={3}>
          <FormattedMessage id="admin.components.dashboard.reporting.companyBenefits.modal.share.benefits" />
        </H4>
        {filteredCompanyBenefits?.length > 0 ? (
          <Flex justifyContent="center" wrap="wrap" gap={1}>
            {filteredCompanyBenefits.map((benefit) => {
              return (
                <Wrapper key={benefit.uuid}>
                  <ImageWrapper>
                    <img src={benefit.cardImage} height={100} />
                  </ImageWrapper>
                  <Body marginTop={1} textAlign="center">
                    {benefit.header}
                  </Body>
                </Wrapper>
              )
            })}
          </Flex>
        ) : (
          <Body textAlign="center" maxWidth="100%">
            <FormattedMessage id="admin.components.dashboard.reporting.companyBenefits.modal.share.noText" />
          </Body>
        )}
      </Loading>
      <Flex justifyContent="space-between" alignItems="center" marginTop={2}>
        <Button tertiary onClick={onClick}>
          <Icon icon={IconTypes.chevronLeft} marginRight />
          <FormattedMessage id="platform.button.back" />
        </Button>
        <CopyToClipboard text={getCopyText()}>
          <Button onClick={handleCopyClick}>
            <FormattedMessage id={copyButtonText} />
          </Button>
        </CopyToClipboard>
      </Flex>
    </div>
  )
}

export default injectIntl(ShareBenefits)
